import React, { useCallback, useEffect, useState } from 'react';
import { Box, Typography } from '@material-ui/core';
import { navigate } from 'gatsby';

import { requiredFields } from '~/utils/helpers';
import { IForm, IValidate } from '@models';

import { JoinUsSuccessModal } from '~/components';
import { Input, Button } from '~/components/ui';

import styles from './styles';
import { useConfirm } from 'material-ui-confirm';

interface IJoinUsFormProps {
    value?: IForm;
    onChange?: (form: IForm) => void;
    onSubmit?: (
        form: IForm & { pageName: string; pageUri: string }
    ) => Promise<void>;
}

export const JoinUsForm = ({ value, onSubmit, onChange }: IJoinUsFormProps) => {
    const classes = styles();
    const confirm = useConfirm();
    const [form, setForm] = useState<IForm>({});
    const [validates, setValidate] = useState<IValidate<IForm>>({
        onSubmit: false,
        errorFields: {},
        valid: true,
    });
    const [openModal, setModal] = useState<boolean>(false);

    useEffect(() => {
        if (value) {
            setForm(value);
        }
    }, [value]);

    const handleChange = (name: keyof IForm) => {
        return useCallback(
            (e: React.ChangeEvent<HTMLInputElement>) => {
                const newForm = { ...form, [name]: e.target.value };
                if (onChange) {
                    onChange(newForm);
                }
                setForm(newForm);
            },
            [form, onChange, setForm]
        );
    };

    const validation = () => {
        const fields = [
            'firstname',
            'lastname',
            'email',
            'phone',
            'dental_practice_name',
            'website',
        ];
        const checkRequiredFields = requiredFields(fields, form);
        setValidate({ onSubmit: true, ...checkRequiredFields });
        return checkRequiredFields.valid;
    };

    const handleSubmit = async () => {
        if (onSubmit && validation()) {
            try {
                await onSubmit({
                    ...form,
                    pageUri: window.location.href,
                    pageName: document.title,
                });
                setValidate({ onSubmit: false, errorFields: {}, valid: true });
                setModal(true);
                setForm({});
            } catch (error) {
                confirm({
                    title: 'Error',
                    cancellationText: '',
                    description: error,
                }).then();
            }
        }
    };

    return (
        <>
            <JoinUsSuccessModal
                open={openModal}
                onClose={() => setModal(false)}
            />
            <Box className={classes.wrapper}>
                <Box className={classes.title}>
                    <Typography
                        variant="subtitle2"
                        className={classes.subtitle}
                    >
                        Become a provider today:
                    </Typography>
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="First Name"
                        name="firstname"
                        value={form.firstname || ''}
                        error={
                            validates.onSubmit &&
                            validates.errorFields?.firstname
                        }
                        onChange={handleChange('firstname')}
                    />
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="Last Name"
                        name="lastname"
                        value={form.lastname || ''}
                        error={
                            validates.onSubmit &&
                            validates.errorFields?.lastname
                        }
                        onChange={handleChange('lastname')}
                    />
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="Email"
                        name="email"
                        value={form.email || ''}
                        error={
                            validates.onSubmit && validates.errorFields?.email
                        }
                        onChange={handleChange('email')}
                    />
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="Phone Number"
                        name="phone"
                        value={form.phone || ''}
                        error={
                            validates.onSubmit && validates.errorFields?.phone
                        }
                        onChange={handleChange('phone')}
                    />
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="Dental Practice Name"
                        name="dental_practice_name"
                        value={form.dental_practice_name || ''}
                        error={
                            validates.onSubmit &&
                            validates.errorFields?.dental_practice_name
                        }
                        onChange={handleChange('dental_practice_name')}
                    />
                </Box>
                <Box className={classes.item}>
                    <Input
                        size="lg"
                        placeholder="Website"
                        name="website"
                        value={form.website || ''}
                        error={
                            validates.onSubmit && validates.errorFields?.website
                        }
                        onChange={handleChange('website')}
                    />
                </Box>
                <Box className={classes.actions}>
                    <Button
                        disabled={
                            !Object.values(form).some(
                                (inputValue) => inputValue
                            )
                        }
                        variant="primary"
                        onClick={handleSubmit}
                        title="Join us"
                    />
                    <Button
                        onClick={() => navigate('/patients')}
                        arrow
                        variant="plain"
                        title="Not a Dentist"
                    />
                </Box>
            </Box>
        </>
    );
};

export default JoinUsForm;
